import { useState, useMemo } from 'react';
import { X, GitMerge, AlertTriangle, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fusion de deux comptes clients.
 * Le "target" (celui affiché en haut) survit ; le "source" (choisi dans la liste)
 * disparaît, toutes ses commandes / dettes / paiements sont migrés vers target.
 * Un enregistrement d'historique est créé côté backend (merged_from).
 */
export const MergeUsersModal = ({ target, allUsers, onClose, onMerged }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  const candidates = useMemo(() => {
    if (!target) return [];
    const q = search.trim().toLowerCase();
    return allUsers
      .filter((u) =>
        u.id !== target.id &&
        u.role === 'customer' &&
        (
          !q ||
          (u.name || '').toLowerCase().includes(q) ||
          (u.phone || '').includes(q) ||
          (u.email || '').toLowerCase().includes(q)
        )
      )
      .slice(0, 25);
  }, [allUsers, target, search]);

  const doMerge = async () => {
    if (!selected) return;
    setMerging(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/users/${target.id}/merge`,
        { source_id: selected.id }
      );
      toast.success(
        `Fusion terminée — ${res.data.orders_moved} commandes, ${res.data.debts_moved} dettes, ${res.data.payments_moved} paiements déplacés`
      );
      onMerged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la fusion');
    } finally {
      setMerging(false);
    }
  };

  if (!target) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="merge-modal"
    >
      <div
        className="bg-[color:var(--color-surface)] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[color:var(--color-primary)]" />
            <h2 className="text-white text-lg">Fusionner un client dans</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="merge-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Target card (survivor) */}
        <div className="p-4 border-b border-white/10 bg-[color:var(--color-surface)]">
          <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Client conservé (cible)</p>
          <p className="text-white">{target.name || '—'}</p>
          <p className="text-zinc-400 text-sm">{target.phone} {target.email && <span className="text-zinc-600 ml-2">· {target.email}</span>}</p>
        </div>

        {confirmOpen ? (
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3 p-3 border border-amber-500/40 bg-amber-500/5">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-amber-100 text-sm">
                <p className="mb-2">Vous êtes sur le point de fusionner :</p>
                <div className="flex items-center gap-2 text-white bg-black/40 p-2 mb-1">
                  <span className="flex-1"><b>{selected.name || '—'}</b> — {selected.phone}</span>
                  <ArrowRight className="w-4 h-4 text-emerald-400" />
                  <span className="flex-1"><b>{target.name || '—'}</b> — {target.phone}</span>
                </div>
                <p className="text-xs">
                  Toutes les commandes, dettes et paiements du client source seront déplacés vers la cible.
                  Le compte source sera <b>définitivement supprimé</b>. Un historique de fusion est conservé sur le compte cible.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={merging}
                className="border border-white/10 text-zinc-300 hover:text-white px-4 py-2 text-sm transition-colors"
                data-testid="merge-cancel-btn"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={doMerge}
                disabled={merging}
                className="bg-[color:var(--color-primary)] text-black hover:bg-[color:var(--color-primary-hover)] px-4 py-2 text-sm transition-colors disabled:opacity-50"
                data-testid="merge-confirm-btn"
              >
                {merging ? 'Fusion en cours…' : 'Confirmer la fusion'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un client (nom, téléphone, email)…"
                  className="w-full pl-9 pr-3 py-2 bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white text-sm outline-none"
                  data-testid="merge-search-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {candidates.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center p-6">Aucun client correspondant.</p>
              ) : (
                candidates.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { setSelected(u); setConfirmOpen(true); }}
                    className="w-full text-left p-3 border-b border-white/5 hover:bg-white/5 transition-colors flex items-center justify-between gap-3"
                    data-testid={`merge-candidate-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">{u.name || '—'}</p>
                      <p className="text-zinc-500 text-xs truncate">{u.phone} {u.email && `· ${u.email}`}</p>
                    </div>
                    <span className="text-zinc-600 text-xs flex-shrink-0">
                      {u.source === 'pos_walkin' ? 'Comptoir' : 'Web'}
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MergeUsersModal;
